<div data-spartan-page class="p-6 md:p-8 space-y-6" style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <div class="flex items-end justify-between gap-3">
    <div>
      <p class="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">Shopee · Customs</p>
      <h1 class="text-2xl font-semibold tracking-tight text-foreground mb-1">Customs Clearance Info Dashboard</h1>
      <p class="text-sm text-muted-foreground mt-1">Process customs clearance for cartons, view pending clearances, and track clearance history</p>
    </div>
  </div>

  <!-- Alerts -->
  @if (errorMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/15 dark:border-red-500/30 p-3 text-sm text-red-800 dark:text-red-300">
      <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z"/></svg>
      <div class="flex-1">{{ errorMessage }}</div>
      <button class="appearance-none border-0 bg-transparent p-0 text-red-500 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 rounded" (click)="clearMessages()" aria-label="Close">
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>
  }
  @if (successMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-500/15 dark:border-green-500/30 p-3 text-sm text-green-800 dark:text-green-300">
      <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
      <div class="flex-1">{{ successMessage }}</div>
      <button class="appearance-none border-0 bg-transparent p-0 text-green-500 hover:text-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 rounded" (click)="clearMessages()" aria-label="Close">
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>
  }

  <!-- Tab Navigation -->
  <div>
    <div class="inline-flex items-center gap-1 p-1 rounded-lg bg-surface-raised border border-border">
      <button type="button"
        class="h-8 px-3.5 text-[13px] font-medium rounded-md border-0 transition-all"
        [class.bg-background]="isClearanceTabActive()"
        [class.text-foreground]="isClearanceTabActive()"
        [class.shadow-sm]="isClearanceTabActive()"
        [class.text-muted-foreground]="!isClearanceTabActive()"
        [class.bg-transparent]="!isClearanceTabActive()"
        (click)="switchTab('clearance')">
        Single Clearance
      </button>
      <button type="button"
        class="h-8 px-3.5 text-[13px] font-medium rounded-md border-0 transition-all"
        [class.bg-background]="isMultipleTabActive()"
        [class.text-foreground]="isMultipleTabActive()"
        [class.shadow-sm]="isMultipleTabActive()"
        [class.text-muted-foreground]="!isMultipleTabActive()"
        [class.bg-transparent]="!isMultipleTabActive()"
        (click)="switchTab('multiple')">
        Multiple Clearance
      </button>
      <button type="button"
        class="h-8 px-3.5 text-[13px] font-medium rounded-md border-0 transition-all"
        [class.bg-background]="isPendingTabActive()"
        [class.text-foreground]="isPendingTabActive()"
        [class.shadow-sm]="isPendingTabActive()"
        [class.text-muted-foreground]="!isPendingTabActive()"
        [class.bg-transparent]="!isPendingTabActive()"
        (click)="switchTab('pending')">
        Pending Clearances
      </button>
      <button type="button"
        class="h-8 px-3.5 text-[13px] font-medium rounded-md border-0 transition-all"
        [class.bg-background]="isHistoryTabActive()"
        [class.text-foreground]="isHistoryTabActive()"
        [class.shadow-sm]="isHistoryTabActive()"
        [class.text-muted-foreground]="!isHistoryTabActive()"
        [class.bg-transparent]="!isHistoryTabActive()"
        (click)="switchTab('history')">
        Clearance History
      </button>
    </div>
  </div>

  <!-- ===== SINGLE CLEARANCE TAB ===== -->
  @if (isClearanceTabActive()) {

    <!-- Eligible Cartons Card -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 class="text-sm font-semibold text-foreground">Eligible Cartons for Customs Clearance</h2>
        <button class="h-8 px-3 text-[13px] border border-border-strong bg-surface text-foreground rounded-md hover:bg-surface-raised transition-colors"
          (click)="toggleEligibleCartons()"
          [disabled]="eligibleCartonsLoading">
          {{ showEligibleCartons ? 'Hide Cartons' : 'Show Cartons' }}
        </button>
      </div>

      @if (showEligibleCartons) {
        <div class="p-4">
          @if (eligibleCartonsLoading) {
            <div class="flex flex-col items-center justify-center py-10 gap-2">
              <div class="h-6 w-6 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
              <p class="text-sm text-muted-foreground">Loading cartons…</p>
            </div>
          } @else if (eligibleCartons.length === 0) {
            <div class="flex flex-col items-center justify-center py-10 gap-2">
              <svg class="size-10 text-border" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15"/></svg>
              <p class="text-sm text-muted-foreground">No eligible cartons found for customs clearance</p>
            </div>
          } @else {
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                    <th class="text-left px-4 py-2.5 font-medium">Carrier TN</th>
                    <th class="text-left px-4 py-2.5 font-medium">Order ID</th>
                    <th class="text-left px-4 py-2.5 font-medium">Status</th>
                    <th class="text-left px-4 py-2.5 font-medium">Destination</th>
                    <th class="text-left px-4 py-2.5 font-medium">Weight (g)</th>
                    <th class="text-left px-4 py-2.5 font-medium">Repacked Date</th>
                    <th class="text-left px-4 py-2.5 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  @for (carton of eligibleCartons; track carton.carrierTrackingNumber) {
                    <tr class="h-10 border-b border-border last:border-0 transition-colors hover:bg-surface-raised"
                      [class.bg-blue-50]="selectedCarton?.carrierTrackingNumber === carton.carrierTrackingNumber">
                      <td class="px-4 font-mono text-[12.5px] font-medium text-foreground">{{ carton.carrierTrackingNumber }}</td>
                      <td class="px-4 text-muted-foreground text-xs">{{ carton.orderId }}</td>
                      <td class="px-4">
                        <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium border"
                          [class.bg-blue-50]="carton.status?.toLowerCase() === 'repacked'"
                          [class.text-blue-700]="carton.status?.toLowerCase() === 'repacked'"
                          [class.border-blue-200]="carton.status?.toLowerCase() === 'repacked'"
                          [class.bg-amber-50]="carton.status?.toLowerCase() === 'intransit'"
                          [class.text-amber-700]="carton.status?.toLowerCase() === 'intransit'"
                          [class.border-amber-200]="carton.status?.toLowerCase() === 'intransit'"
                          [class.bg-green-50]="carton.status?.toLowerCase() === 'delivered' || carton.status?.toLowerCase() === 'customs_cleared'"
                          [class.text-green-700]="carton.status?.toLowerCase() === 'delivered' || carton.status?.toLowerCase() === 'customs_cleared'"
                          [class.border-green-200]="carton.status?.toLowerCase() === 'delivered' || carton.status?.toLowerCase() === 'customs_cleared'"
                          [class.bg-surface-raised]="!['repacked','intransit','delivered','customs_cleared'].includes(carton.status?.toLowerCase() ?? '')"
                          [class.text-muted-foreground]="!['repacked','intransit','delivered','customs_cleared'].includes(carton.status?.toLowerCase() ?? '')"
                          [class.border-border]="!['repacked','intransit','delivered','customs_cleared'].includes(carton.status?.toLowerCase() ?? '')">
                          {{ getCartonStatusLabel(carton.status) }}
                        </span>
                      </td>
                      <td class="px-4 text-muted-foreground text-xs">{{ carton.destinationRegionName || carton.destinationRegion || 'N/A' }}</td>
                      <td class="px-4 text-muted-foreground text-sm">{{ carton.weight }}</td>
                      <td class="px-4 text-muted-foreground text-xs">{{ formatDate(carton.repackedDate) }}</td>
                      <td class="px-4">
                        <button class="h-7 px-2.5 text-[13px] bg-accent text-accent-foreground border-0 rounded-md hover:bg-accent/90 transition-colors disabled:opacity-50"
                          (click)="selectCartonForClearance(carton)"
                          [disabled]="isLoading">
                          Select
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            @if (totalCartonPages > 1) {
              <div class="flex items-center justify-center gap-1 pt-3">
                <button class="h-8 px-2.5 text-[13px] border border-border-strong bg-surface text-foreground rounded-md disabled:opacity-40 hover:bg-surface-raised transition-colors"
                  [disabled]="cartonFilters.pageNumber === 1"
                  (click)="changeCartonPage(cartonFilters.pageNumber - 1)">Previous</button>
                @for (page of [].constructor(totalCartonPages); track page; let i = $index) {
                  <button class="h-8 w-8 text-[13px] rounded-md border transition-colors"
                    [class.border-accent]="cartonFilters.pageNumber === i + 1"
                    [class.bg-blue-50]="cartonFilters.pageNumber === i + 1"
                    [class.text-accent]="cartonFilters.pageNumber === i + 1"
                    [class.border-border-strong]="cartonFilters.pageNumber !== i + 1"
                    [class.bg-surface]="cartonFilters.pageNumber !== i + 1"
                    [class.text-foreground]="cartonFilters.pageNumber !== i + 1"
                    (click)="changeCartonPage(i + 1)">{{ i + 1 }}</button>
                }
                <button class="h-8 px-2.5 text-[13px] border border-border-strong bg-surface text-foreground rounded-md disabled:opacity-40 hover:bg-surface-raised transition-colors"
                  [disabled]="cartonFilters.pageNumber === totalCartonPages"
                  (click)="changeCartonPage(cartonFilters.pageNumber + 1)">Next</button>
              </div>
            }
          }

          @if (selectedCarton) {
            <div class="flex items-center justify-between gap-2 rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 p-3 mt-3 text-sm text-blue-700 dark:text-blue-300">
              <div>
                <strong>Selected Carton:</strong> {{ selectedCarton.carrierTrackingNumber }}
                (Order: {{ selectedCarton.orderId }}, Status: {{ getCartonStatusLabel(selectedCarton.status) }})
              </div>
              <button class="h-7 px-2.5 text-[13px] border border-blue-200 dark:border-blue-500/30 bg-background text-blue-600 dark:text-blue-300 rounded-md hover:bg-surface-raised transition-colors"
                (click)="clearSelectedCarton()">Clear</button>
            </div>
          }
        </div>
      }
    </div>

    <!-- Customs Clearance Form Card -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border">
        <h2 class="text-sm font-semibold text-foreground">Customs Clearance Request</h2>
      </div>
      <div class="p-4">
        <form (ngSubmit)="submitCustomsClearance()" #clearanceForm="ngForm">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <div>
              <label for="carrierTN" class="block text-xs font-medium text-muted-foreground mb-1.5">Carrier Tracking Number <span class="text-red-500">*</span></label>
              <input type="text" id="carrierTN"
                class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition"
                [(ngModel)]="customsClearance.carrier_tn" name="carrierTN" required
                placeholder="Enter carrier tracking number" />
            </div>

            <div>
              <label for="destinationRegion" class="block text-xs font-medium text-muted-foreground mb-1.5">Destination Region <span class="text-red-500">*</span></label>
              <div class="relative">
                <select id="destinationRegion"
                  class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                  [(ngModel)]="customsClearance.destination_region" name="destinationRegion" required>
                  <option value="">Select destination region</option>
                  @for (region of regions; track region.value) {
                    <option [value]="region.value">{{ region.label }}</option>
                  }
                </select>
                <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>

            <div>
              <label for="customsRegion" class="block text-xs font-medium text-muted-foreground mb-1.5">Customs Region <span class="text-red-500">*</span></label>
              <div class="relative">
                <select id="customsRegion"
                  class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                  [(ngModel)]="customsClearance.customs_region" name="customsRegion" required>
                  <option value="">Select customs region</option>
                  @for (region of regions; track region.value) {
                    <option [value]="region.value">{{ region.label }}</option>
                  }
                </select>
                <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>

            <div>
              <label for="customsDirection" class="block text-xs font-medium text-muted-foreground mb-1.5">Customs Direction <span class="text-red-500">*</span></label>
              <div class="relative">
                <select id="customsDirection"
                  class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                  [(ngModel)]="customsClearance.customs_dir" name="customsDirection" required>
                  <option value="">Select customs direction</option>
                  @for (direction of customsDirections; track direction.value) {
                    <option [value]="direction.value">{{ direction.label }}</option>
                  }
                </select>
                <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>

            <div>
              <label for="brokerName" class="block text-xs font-medium text-muted-foreground mb-1.5">Broker Name <span class="text-red-500">*</span></label>
              <input type="text" id="brokerName"
                class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition"
                [(ngModel)]="customsClearance.cc_broker_name" name="brokerName" required
                placeholder="Enter broker name" />
            </div>

            <div>
              <label for="portCode" class="block text-xs font-medium text-muted-foreground mb-1.5">Port Code <span class="text-red-500">*</span></label>
              <input type="text" id="portCode"
                class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition"
                [(ngModel)]="customsClearance.cc_port_code" name="portCode" required
                placeholder="Enter port code" />
            </div>

            <div>
              <label for="clearanceWay" class="block text-xs font-medium text-muted-foreground mb-1.5">Clearance Way <span class="text-red-500">*</span></label>
              <div class="relative">
                <select id="clearanceWay"
                  class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                  [(ngModel)]="customsClearance.cc_way" name="clearanceWay" required>
                  <option value="">Select clearance way</option>
                  @for (way of clearanceWays; track way.value) {
                    <option [value]="way.value">{{ way.label }}</option>
                  }
                </select>
                <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>

          </div>

          <div class="flex items-center gap-2 pt-5">
            <button type="submit"
              class="h-9 px-4 text-sm bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
              [disabled]="isLoading || !isCustomsClearanceValid()">
              @if (isLoading) {
                <span class="inline-block h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin mr-1.5 align-middle"></span>
              }
              Submit Clearance Request
            </button>
            <button type="button"
              class="h-9 px-4 text-sm border border-border-strong bg-surface text-foreground rounded-md hover:bg-surface-raised transition-colors disabled:opacity-50"
              (click)="resetCustomsClearanceForm()"
              [disabled]="isLoading">
              Reset Form
            </button>
          </div>
        </form>
      </div>
    </div>
  }

  <!-- ===== MULTIPLE CLEARANCE TAB ===== -->
  @if (isMultipleTabActive()) {

    <div>
      <p class="font-semibold text-base text-foreground mb-1">Multiple Customs Clearance</p>
      <p class="text-sm text-muted-foreground">Select multiple cartons to submit clearance information in batch.</p>
    </div>

    <!-- Filters Card -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border">
        <h2 class="text-sm font-semibold text-foreground">Filters</h2>
      </div>
      <div class="p-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label for="multipleCartonNumber" class="block text-xs font-medium text-muted-foreground mb-1.5">Carton Number</label>
            <input type="text" id="multipleCartonNumber"
              class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition"
              [(ngModel)]="multipleCartonFilters.cartonNumber"
              placeholder="Enter carton number"
              (keyup.enter)="loadMultipleCartons()" />
          </div>
          <div>
            <label for="multipleOrderId" class="block text-xs font-medium text-muted-foreground mb-1.5">Order ID</label>
            <input type="text" id="multipleOrderId"
              class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition"
              [(ngModel)]="multipleCartonFilters.orderId"
              placeholder="Enter order ID"
              (keyup.enter)="loadMultipleCartons()" />
          </div>
          <div>
            <label for="multipleStatus" class="block text-xs font-medium text-muted-foreground mb-1.5">Status</label>
            <div class="relative">
              <select id="multipleStatus"
                class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                [(ngModel)]="multipleCartonFilters.status">
                <option value="">All Statuses</option>
                <option value="Repacked">Repacked</option>
                <option value="InTransit">In Transit</option>
                <option value="ArrivedAtDestination">Arrived at Destination</option>
              </select>
              <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>
          <div class="flex items-end gap-2">
            <button type="button"
              class="h-9 px-3.5 text-sm bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors"
              (click)="loadMultipleCartons()">
              Search
            </button>
            <button type="button"
              class="h-9 px-3.5 text-sm border border-border-strong bg-surface text-foreground rounded-md hover:bg-surface-raised transition-colors"
              (click)="clearMultipleFilters()">
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Selection Actions -->
    @if (multipleCartons.length > 0) {
      <div hlmCard>
        <div class="px-4 py-3 flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <input class="w-4 h-4 rounded border-border-strong" type="checkbox"
              id="selectAllMultiple"
              [checked]="isAllMultipleCartonsSelected()"
              [indeterminate]="isSomeMultipleCartonsSelected()"
              (change)="toggleAllMultipleCartons()" />
            <label for="selectAllMultiple" class="text-sm text-foreground cursor-pointer">
              Select All ({{ selectedMultipleCartons.size }} of {{ multipleCartons.length }} selected)
            </label>
          </div>
          <div class="flex gap-2">
            <button type="button"
              class="h-8 px-3 text-[13px] border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 rounded-md disabled:opacity-40 hover:bg-orange-100 transition-colors"
              [disabled]="selectedMultipleCartons.size === 0"
              (click)="clearMultipleSelection()">
              Clear Selection
            </button>
            <button type="button"
              class="h-8 px-3 text-[13px] bg-accent text-accent-foreground border-0 rounded-md font-medium disabled:opacity-40 hover:bg-accent/90 transition-colors"
              [disabled]="selectedMultipleCartons.size === 0 || isMultipleBatchProcessing"
              (click)="openMultipleClearanceModal()">
              Submit Clearance ({{ selectedMultipleCartons.size }})
            </button>
          </div>
        </div>
      </div>
    }

    <!-- Cartons Table Card -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 class="text-sm font-semibold text-foreground">Available Cartons</h2>
        @if (isLoadingMultipleCartons) {
          <div class="h-4 w-4 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
        }
      </div>
      <div class="p-4">
        @if (multipleCartons.length === 0 && !isLoadingMultipleCartons) {
          <div class="flex flex-col items-center justify-center py-10 gap-2">
            <svg class="size-10 text-border" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15"/></svg>
            <p class="text-sm text-muted-foreground">No cartons found matching the current filters.</p>
          </div>
        }
        @if (multipleCartons.length > 0) {
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                  <th class="text-left px-4 py-2.5 font-medium w-11">
                    <input class="w-4 h-4 rounded border-border-strong" type="checkbox"
                      [checked]="isAllMultipleCartonsSelected()"
                      [indeterminate]="isSomeMultipleCartonsSelected()"
                      (change)="toggleAllMultipleCartons()" />
                  </th>
                  <th class="text-left px-4 py-2.5 font-medium">Carton Number</th>
                  <th class="text-left px-4 py-2.5 font-medium">Order ID</th>
                  <th class="text-left px-4 py-2.5 font-medium">Status</th>
                  <th class="text-left px-4 py-2.5 font-medium">Repacked Date</th>
                  <th class="text-left px-4 py-2.5 font-medium">Weight (kg)</th>
                  <th class="text-left px-4 py-2.5 font-medium">Dimensions (cm)</th>
                </tr>
              </thead>
              <tbody>
                @for (carton of multipleCartons; track carton.carrierTrackingNumber) {
                  <tr class="h-10 border-b border-border last:border-0 transition-colors cursor-pointer hover:bg-surface-raised"
                    [class.bg-blue-50]="selectedMultipleCartons.has(carton.carrierTrackingNumber)"
                    (click)="toggleMultipleCartonSelection(carton.carrierTrackingNumber)">
                    <td class="px-4">
                      <input class="w-4 h-4 rounded border-border-strong" type="checkbox"
                        [checked]="selectedMultipleCartons.has(carton.carrierTrackingNumber)"
                        (click)="$event.stopPropagation()"
                        (change)="toggleMultipleCartonSelection(carton.carrierTrackingNumber)" />
                    </td>
                    <td class="px-4 font-mono text-[12.5px] font-medium text-foreground">{{ carton.carrierTrackingNumber }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ carton.orderId }}</td>
                    <td class="px-4">
                      <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium border bg-surface-raised text-muted-foreground border-border">
                        {{ getCartonStatusLabel(carton.status) }}
                      </span>
                    </td>
                    <td class="px-4 text-muted-foreground text-xs">{{ formatDate(carton.repackedDate) }}</td>
                    <td class="px-4 text-muted-foreground text-sm">{{ carton.weight | number:'1.2-2' }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ carton.length }} × {{ carton.width }} × {{ carton.height }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          @if (multipleCartonPagination && multipleCartonPagination.totalPages > 1) {
            <div class="flex items-center justify-center gap-1 pt-3">
              <button class="h-8 px-2.5 text-[13px] border border-border-strong bg-surface text-foreground rounded-md disabled:opacity-40 hover:bg-surface-raised transition-colors"
                [disabled]="multipleCartonFilters.pageNumber === 1"
                (click)="changeMultipleCartonPage(multipleCartonFilters.pageNumber - 1)">Previous</button>
              @for (i of getPageNumbers(multipleCartonPagination.totalPages); track i) {
                <button class="h-8 w-8 text-[13px] rounded-md border transition-colors"
                  [class.border-accent]="i + 1 === multipleCartonFilters.pageNumber"
                  [class.bg-blue-50]="i + 1 === multipleCartonFilters.pageNumber"
                  [class.text-accent]="i + 1 === multipleCartonFilters.pageNumber"
                  [class.border-border-strong]="i + 1 !== multipleCartonFilters.pageNumber"
                  [class.bg-surface]="i + 1 !== multipleCartonFilters.pageNumber"
                  [class.text-foreground]="i + 1 !== multipleCartonFilters.pageNumber"
                  (click)="changeMultipleCartonPage(i + 1)">{{ i + 1 }}</button>
              }
              <button class="h-8 px-2.5 text-[13px] border border-border-strong bg-surface text-foreground rounded-md disabled:opacity-40 hover:bg-surface-raised transition-colors"
                [disabled]="multipleCartonFilters.pageNumber === multipleCartonPagination.totalPages"
                (click)="changeMultipleCartonPage(multipleCartonFilters.pageNumber + 1)">Next</button>
            </div>
          }
        }
      </div>
    </div>

    <!-- Batch Form (shown when cartons selected) -->
    @if (selectedMultipleCartons.size > 0) {
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">Batch Customs Clearance Information</h2>
        </div>
        <div class="p-4">
          <div class="flex items-start gap-3 rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 p-3 mb-4 text-sm text-blue-700 dark:text-blue-300">
            <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0zm-9-3.75h.008v.008H12V8.25z"/></svg>
            <span><strong>Selected Cartons:</strong> {{ selectedMultipleCartons.size }} carton(s) will be processed with the same customs clearance information.</span>
          </div>

          <form (ngSubmit)="submitBatchClearance()" #multipleClearanceForm="ngForm">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <div>
                <label for="multipleDestinationRegion" class="block text-xs font-medium text-muted-foreground mb-1.5">Destination Region <span class="text-red-500">*</span></label>
                <div class="relative">
                  <select id="multipleDestinationRegion"
                    class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                    [(ngModel)]="batchClearanceForm.destinationRegion" name="multipleDestinationRegion" required>
                    <option value="">Select destination region</option>
                    @for (region of regions; track region.value) {
                      <option [value]="region.value">{{ region.label }}</option>
                    }
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>

              <div>
                <label for="multipleCustomsRegion" class="block text-xs font-medium text-muted-foreground mb-1.5">Customs Region <span class="text-red-500">*</span></label>
                <div class="relative">
                  <select id="multipleCustomsRegion"
                    class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                    [(ngModel)]="batchClearanceForm.customsRegion" name="multipleCustomsRegion" required>
                    <option value="">Select customs region</option>
                    @for (region of regions; track region.value) {
                      <option [value]="region.value">{{ region.label }}</option>
                    }
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>

              <div>
                <label for="multipleCustomsDirection" class="block text-xs font-medium text-muted-foreground mb-1.5">Customs Direction <span class="text-red-500">*</span></label>
                <div class="relative">
                  <select id="multipleCustomsDirection"
                    class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                    [(ngModel)]="batchClearanceForm.customsDirection" name="multipleCustomsDirection" required>
                    <option value="">Select customs direction</option>
                    @for (direction of customsDirections; track direction.value) {
                      <option [value]="direction.value">{{ direction.label }}</option>
                    }
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>

              <div>
                <label for="multipleBrokerName" class="block text-xs font-medium text-muted-foreground mb-1.5">Broker Name <span class="text-red-500">*</span></label>
                <input type="text" id="multipleBrokerName"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition"
                  [(ngModel)]="batchClearanceForm.brokerName" name="multipleBrokerName" required
                  placeholder="Enter broker name" />
              </div>

              <div>
                <label for="multiplePortCode" class="block text-xs font-medium text-muted-foreground mb-1.5">Port Code <span class="text-red-500">*</span></label>
                <input type="text" id="multiplePortCode"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition"
                  [(ngModel)]="batchClearanceForm.portCode" name="multiplePortCode" required
                  placeholder="Enter port code" />
              </div>

              <div>
                <label for="multipleClearanceWay" class="block text-xs font-medium text-muted-foreground mb-1.5">Clearance Way <span class="text-red-500">*</span></label>
                <div class="relative">
                  <select id="multipleClearanceWay"
                    class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                    [(ngModel)]="batchClearanceForm.clearanceWay" name="multipleClearanceWay" required>
                    <option value="">Select clearance way</option>
                    @for (way of clearanceWays; track way.value) {
                      <option [value]="way.value">{{ way.label }}</option>
                    }
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>

              <div class="sm:col-span-2">
                <label for="multipleRemarks" class="block text-xs font-medium text-muted-foreground mb-1.5">Remarks</label>
                <textarea id="multipleRemarks" rows="3"
                  class="w-full border border-border-strong bg-surface rounded-md px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition resize-none"
                  [(ngModel)]="batchClearanceForm.remarks" name="multipleRemarks"
                  placeholder="Enter any additional remarks..."></textarea>
              </div>

            </div>

            <div class="flex items-center gap-2 pt-4">
              <button type="submit"
                class="h-9 px-4 text-sm bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
                [disabled]="isMultipleBatchProcessing || !isBatchFormValid()">
                @if (isMultipleBatchProcessing) {
                  <span class="inline-block h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin mr-1.5 align-middle"></span>
                }
                {{ isMultipleBatchProcessing ? 'Processing...' : 'Submit Batch Clearance' }}
              </button>
              <button type="button"
                class="h-9 px-4 text-sm border border-border-strong bg-surface text-foreground rounded-md hover:bg-surface-raised transition-colors disabled:opacity-50"
                (click)="resetBatchClearanceForm()"
                [disabled]="isMultipleBatchProcessing">
                Reset Form
              </button>
              <button type="button"
                class="h-9 px-4 text-sm border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 rounded-md hover:bg-orange-100 transition-colors disabled:opacity-50"
                (click)="clearMultipleSelection()"
                [disabled]="isMultipleBatchProcessing">
                Clear Selection
              </button>
            </div>

            <!-- Batch Progress -->
            @if (isMultipleBatchProcessing && batchProgress) {
              <div class="flex items-start gap-3 rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 p-3 mt-4">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-2">
                    <div class="h-4 w-4 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
                    <strong class="text-sm font-medium text-blue-700 dark:text-blue-300">Processing Batch Clearance...</strong>
                  </div>
                  <div class="h-1.5 rounded-full bg-blue-200 dark:bg-blue-500/30 overflow-hidden mb-1">
                    <div class="h-1.5 rounded-full bg-accent transition-all"
                      [style.width.%]="(batchProgress.processed / batchProgress.total) * 100"></div>
                  </div>
                  <p class="text-xs text-muted-foreground">Processed {{ batchProgress.processed }} of {{ batchProgress.total }} cartons</p>
                  @if (batchProgress.errors.length > 0) {
                    <div class="mt-2">
                      <p class="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">Errors encountered:</p>
                      <ul class="text-xs list-disc list-inside">
                        @for (error of batchProgress.errors; track error) {
                          <li class="text-red-600 dark:text-red-400">{{ error }}</li>
                        }
                      </ul>
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Batch Results -->
            @if (batchResult) {
              <div class="rounded-lg border p-3 mt-4"
                [class.border-green-200]="batchResult.errorCount === 0"
                [class.bg-green-50]="batchResult.errorCount === 0"
                [class.border-orange-200]="batchResult.errorCount > 0"
                [class.bg-orange-50]="batchResult.errorCount > 0">
                <p class="font-medium text-sm mb-1"
                  [class.text-green-700]="batchResult.errorCount === 0"
                  [class.text-orange-700]="batchResult.errorCount > 0">
                  Batch Processing {{ batchResult.errorCount === 0 ? 'Completed Successfully' : 'Completed with Errors' }}
                </p>
                <p class="text-xs text-muted-foreground mb-0">
                  Successfully processed {{ batchResult.successCount }} cartons.
                  @if (batchResult.errorCount > 0) {
                    {{ batchResult.errorCount }} processing failed.
                  }
                </p>
                @if (batchResult.errors && batchResult.errors.length > 0) {
                  <ul class="mt-2 text-xs list-disc list-inside">
                    @for (error of batchResult.errors; track error.cartonNumber) {
                      <li class="text-red-600 dark:text-red-400">{{ error.cartonNumber }}: {{ error.error }}</li>
                    }
                  </ul>
                }
              </div>
            }
          </form>
        </div>
      </div>
    }
  }

  <!-- ===== PENDING CLEARANCES TAB ===== -->
  @if (isPendingTabActive()) {
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border">
        <h2 class="text-sm font-semibold text-foreground">Pending Customs Clearances</h2>
      </div>
      <div class="p-4">
        @if (pendingClearancesLoading) {
          <div class="flex flex-col items-center justify-center py-10 gap-2">
            <div class="h-6 w-6 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
            <p class="text-sm text-muted-foreground">Loading pending clearances…</p>
          </div>
        } @else if (pendingClearances.length === 0) {
          <div class="flex flex-col items-center justify-center py-10 gap-2">
            <svg class="size-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
            <p class="text-sm text-muted-foreground">No pending customs clearances found</p>
          </div>
        } @else {
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                  <th class="text-left px-4 py-2.5 font-medium">Carrier TN</th>
                  <th class="text-left px-4 py-2.5 font-medium">Status</th>
                  <th class="text-left px-4 py-2.5 font-medium">Customs Region</th>
                  <th class="text-left px-4 py-2.5 font-medium">Direction</th>
                  <th class="text-left px-4 py-2.5 font-medium">Broker</th>
                  <th class="text-left px-4 py-2.5 font-medium">Port Code</th>
                  <th class="text-left px-4 py-2.5 font-medium">Clearance Way</th>
                  <th class="text-left px-4 py-2.5 font-medium">Created</th>
                  <th class="text-left px-4 py-2.5 font-medium">Created By</th>
                </tr>
              </thead>
              <tbody>
                @for (clearance of pendingClearances; track clearance.carrierTrackingNumber) {
                  <tr class="h-10 border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors">
                    <td class="px-4 font-mono text-[12.5px] font-medium text-foreground">{{ clearance.carrierTrackingNumber }}</td>
                    <td class="px-4">
                      <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium border bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30">
                        {{ clearance.customsStatus }}
                      </span>
                    </td>
                    <td class="px-4 text-muted-foreground text-xs">{{ clearance.customsRegion }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ clearance.customsDirection }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ clearance.brokerName }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ clearance.portCode }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ clearance.clearanceWay }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ formatDate(clearance.createdAtUtc) }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ clearance.createdBy }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          @if (totalPendingPages > 1) {
            <div class="flex items-center justify-center gap-1 pt-3">
              <button class="h-8 px-2.5 text-[13px] border border-border-strong bg-surface text-foreground rounded-md disabled:opacity-40 hover:bg-surface-raised transition-colors"
                [disabled]="pendingFilters.pageNumber === 1"
                (click)="changePendingPage(pendingFilters.pageNumber - 1)">Previous</button>
              @for (page of [].constructor(totalPendingPages); track page; let i = $index) {
                <button class="h-8 w-8 text-[13px] rounded-md border transition-colors"
                  [class.border-accent]="pendingFilters.pageNumber === i + 1"
                  [class.bg-blue-50]="pendingFilters.pageNumber === i + 1"
                  [class.text-accent]="pendingFilters.pageNumber === i + 1"
                  [class.border-border-strong]="pendingFilters.pageNumber !== i + 1"
                  [class.bg-surface]="pendingFilters.pageNumber !== i + 1"
                  [class.text-foreground]="pendingFilters.pageNumber !== i + 1"
                  (click)="changePendingPage(i + 1)">{{ i + 1 }}</button>
              }
              <button class="h-8 px-2.5 text-[13px] border border-border-strong bg-surface text-foreground rounded-md disabled:opacity-40 hover:bg-surface-raised transition-colors"
                [disabled]="pendingFilters.pageNumber === totalPendingPages"
                (click)="changePendingPage(pendingFilters.pageNumber + 1)">Next</button>
            </div>
          }
        }
      </div>
    </div>
  }

  <!-- ===== HISTORY TAB ===== -->
  @if (isHistoryTabActive()) {
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border">
        <h2 class="text-sm font-semibold text-foreground">Customs Clearance History</h2>
      </div>
      <div class="p-4">
        <div class="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end mb-4">
          <div>
            <label for="carrierTNSearch" class="block text-xs font-medium text-muted-foreground mb-1.5">Carrier Tracking Number</label>
            <input type="text" id="carrierTNSearch"
              class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition"
              [(ngModel)]="selectedCarrierTN"
              placeholder="Enter carrier tracking number to search history" />
          </div>
          <button class="h-9 px-4 text-sm bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
            (click)="loadClearanceHistory()"
            [disabled]="historyLoading">
            @if (historyLoading) {
              <span class="inline-block h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin mr-1.5 align-middle"></span>
            }
            Search History
          </button>
        </div>

        @if (historyLoading) {
          <div class="flex flex-col items-center justify-center py-10 gap-2">
            <div class="h-6 w-6 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
            <p class="text-sm text-muted-foreground">Loading history…</p>
          </div>
        } @else if (clearanceHistory.length > 0) {
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                  <th class="text-left px-4 py-2.5 font-medium">ID</th>
                  <th class="text-left px-4 py-2.5 font-medium">Status</th>
                  <th class="text-left px-4 py-2.5 font-medium">Customs Region</th>
                  <th class="text-left px-4 py-2.5 font-medium">Direction</th>
                  <th class="text-left px-4 py-2.5 font-medium">Broker</th>
                  <th class="text-left px-4 py-2.5 font-medium">Port Code</th>
                  <th class="text-left px-4 py-2.5 font-medium">Clearance Way</th>
                  <th class="text-left px-4 py-2.5 font-medium">Success</th>
                  <th class="text-left px-4 py-2.5 font-medium">Created</th>
                  <th class="text-left px-4 py-2.5 font-medium">Cleared</th>
                </tr>
              </thead>
              <tbody>
                @for (event of clearanceHistory; track event.id) {
                  <tr class="h-10 border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors">
                    <td class="px-4 font-mono text-xs text-muted-foreground">{{ event.id.substring(0, 8) }}…</td>
                    <td class="px-4">
                      <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium border bg-surface-raised text-muted-foreground border-border">
                        {{ event.customsStatus }}
                      </span>
                    </td>
                    <td class="px-4 text-muted-foreground text-xs">{{ event.customsRegion }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ event.customsDirection }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ event.brokerName }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ event.portCode }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ event.clearanceWay }}</td>
                    <td class="px-4">
                      <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium border"
                        [class.bg-green-50]="event.wasSuccessful"
                        [class.text-green-700]="event.wasSuccessful"
                        [class.border-green-200]="event.wasSuccessful"
                        [class.bg-red-50]="!event.wasSuccessful"
                        [class.text-red-700]="!event.wasSuccessful"
                        [class.border-red-200]="!event.wasSuccessful">
                        {{ event.wasSuccessful ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td class="px-4 text-muted-foreground text-xs">{{ formatDate(event.createdAtUtc) }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ formatDate(event.clearedAtUtc) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else if (selectedCarrierTN) {
          <div class="flex flex-col items-center justify-center py-10 gap-2">
            <svg class="size-10 text-border" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15"/></svg>
            <p class="text-sm text-muted-foreground">No clearance history found for {{ selectedCarrierTN }}</p>
          </div>
        }
      </div>
    </div>
  }

</div>

<!-- Multiple Clearance Modal (Bootstrap — preserved for JS API) -->
<div class="modal fade" id="multipleClearanceModal" tabindex="-1" aria-labelledby="multipleClearanceModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-lg">
    <div class="modal-content" style="border-color: rgb(var(--ds-border, 231 229 228)); border-radius: 8px;">
      <div class="modal-header" style="border-color: rgb(var(--ds-border, 231 229 228)); padding: 1rem 1.25rem;">
        <h5 class="modal-title fw-semibold" id="multipleClearanceModalLabel" style="font-size: 1rem;">
          Batch Customs Clearance
        </h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body" style="padding: 1.25rem;">
        @if (!isMultipleBatchProcessing) {
          <p class="text-muted mb-3" style="font-size: .875rem;">
            Submit customs clearance information for {{ selectedMultipleCartons.size }} selected carton(s).
          </p>

          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label for="batchCustomsStatus" class="form-label" style="font-size: .8125rem; font-weight: 500;">Customs Status <span class="text-danger">*</span></label>
              <select id="batchCustomsStatus" class="form-select"
                style="height: 38px; border-color: #D6D3D1; font-size: .875rem; border-radius: 6px;"
                [(ngModel)]="batchClearanceForm.customsStatus" required>
                <option value="">Select Status</option>
                <option value="PendingDocuments">Pending Documents</option>
                <option value="UnderReview">Under Review</option>
                <option value="PendingPayment">Pending Payment</option>
                <option value="Cleared">Cleared</option>
                <option value="OnHold">On Hold</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div class="col-md-6">
              <label for="batchClearanceDate" class="form-label" style="font-size: .8125rem; font-weight: 500;">Clearance Date</label>
              <input type="datetime-local" id="batchClearanceDate" class="form-control"
                style="height: 38px; border-color: #D6D3D1; font-size: .875rem; border-radius: 6px;"
                [(ngModel)]="batchClearanceForm.clearedAtUtc" />
            </div>
            <div class="col-12">
              <label for="batchRemarks" class="form-label" style="font-size: .8125rem; font-weight: 500;">Remarks</label>
              <textarea id="batchRemarks" class="form-control" rows="3"
                style="border-color: #D6D3D1; font-size: .875rem; border-radius: 6px;"
                [(ngModel)]="batchClearanceForm.remarks"
                placeholder="Enter any additional remarks..."></textarea>
            </div>
          </div>

          <div>
            <p class="mb-2 fw-medium" style="font-size: .875rem;">Selected Cartons:</p>
            <div style="max-height: 200px; overflow-y: auto; border: 1px solid #E7E5E4; border-radius: 6px;">
              @for (carton of getSelectedMultipleCartonsArray(); track carton.carrierTrackingNumber) {
                <div class="d-flex align-items-center justify-content-between px-3 py-2" style="border-bottom: 1px solid #F5F5F4;">
                  <div>
                    <p class="mb-0 fw-medium font-monospace" style="font-size: .8125rem;">{{ carton.carrierTrackingNumber }}</p>
                    <p class="mb-0 text-muted" style="font-size: .75rem;">Order: {{ carton.orderId }}</p>
                  </div>
                  <span class="badge" [class]="getCartonStatusBadgeClass(carton.status)">{{ getCartonStatusLabel(carton.status) }}</span>
                </div>
              }
            </div>
          </div>
        }

        @if (isMultipleBatchProcessing) {
          <div class="text-center py-3">
            <div class="spinner-border mb-3" style="color: #2563EB;" role="status">
              <span class="visually-hidden">Processing...</span>
            </div>
            <p class="fw-medium mb-1" style="font-size: 1rem;">Processing Batch Clearance</p>
            <p class="text-muted mb-3" style="font-size: .875rem;">{{ batchProgress.processed }} of {{ batchProgress.total }} cartons processed</p>
            <div class="progress mb-3" style="height: 8px;">
              <div class="progress-bar" style="background: #2563EB;"
                [style.width.%]="(batchProgress.processed / batchProgress.total) * 100"
                [attr.aria-valuenow]="batchProgress.processed"
                [attr.aria-valuemax]="batchProgress.total">
                {{ ((batchProgress.processed / batchProgress.total) * 100) | number:'1.0-0' }}%
              </div>
            </div>
          </div>
        }

        @if (batchResult) {
          <div class="d-flex align-items-start gap-2 rounded-2 border p-3"
            [style.border-color]="batchResult.successCount > 0 ? '#BBF7D0' : '#FECACA'"
            [style.background]="batchResult.successCount > 0 ? '#F0FDF4' : '#FEF2F2'">
            <div class="flex-grow-1">
              <p class="mb-1 fw-medium" style="font-size: .875rem;"
                [style.color]="batchResult.successCount > 0 ? '#15803D' : '#B91C1C'">
                Batch Processing Complete
              </p>
              <p class="mb-0 text-muted" style="font-size: .875rem;">
                <strong>{{ batchResult.successCount }}</strong> carton(s) processed successfully.
                @if (batchResult.errorCount > 0) {
                  <strong>{{ batchResult.errorCount }}</strong> carton(s) failed.
                }
              </p>
            </div>
          </div>
          @if (batchResult.errors.length > 0) {
            <div class="mt-3">
              <p class="fw-medium mb-2" style="font-size: .875rem;">Failed Cartons:</p>
              @for (error of batchResult.errors; track error.cartonNumber) {
                <div class="d-flex align-items-center justify-content-between px-3 py-2 rounded-2 mb-1" style="background: #FEF2F2; font-size: .8125rem;">
                  <span class="fw-medium font-monospace" style="color: #B91C1C;">{{ error.cartonNumber }}</span>
                  <span class="text-danger">{{ error.error }}</span>
                </div>
              }
            </div>
          }
        }
      </div>
      <div class="modal-footer" style="border-color: #E7E5E4; padding: .75rem 1.25rem;">
        <button type="button" class="btn btn-sm"
          style="height: 36px; padding: 0 14px; font-size: .875rem; border: 1px solid #D6D3D1; background: #fff; color: #1C1917; border-radius: 6px;"
          data-bs-dismiss="modal">
          {{ isMultipleBatchProcessing ? 'Close' : 'Cancel' }}
        </button>
        @if (!isMultipleBatchProcessing && !batchResult) {
          <button type="button" class="btn btn-sm"
            style="height: 36px; padding: 0 14px; font-size: .875rem; background: #2563EB; color: #fff; border: none; border-radius: 6px;"
            [disabled]="!batchClearanceForm.customsStatus"
            (click)="submitBatchClearance()">
            Submit Clearance
          </button>
        }
        @if (batchResult) {
          <button type="button" class="btn btn-sm"
            style="height: 36px; padding: 0 14px; font-size: .875rem; background: #16A34A; color: #fff; border: none; border-radius: 6px;"
            data-bs-dismiss="modal"
            (click)="resetBatchClearance()">
            Done
          </button>
        }
      </div>
    </div>
  </div>
</div>
